/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { stripeService } from '../lib/stripe';
import { Loader2, FileText, ArrowRight, CheckCircle, XCircle, Clock, CreditCard, Calendar, Building2, AlertCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<any>(null);
  console.log(subscription, "subscription in BillingPage");
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);

        // Get customer ID first
        const { data: customer, error: customerError } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (customerError) throw customerError;
        if (!customer) throw new Error('No customer record found');

        // Get subscription data
        const sub = await stripeService.getUserSubscription(user.id);
        setSubscription(sub);

        // Get invoices
        const invs = await stripeService.getCustomerInvoices(customer.customer_id);
        console.log('Fetched invoices:', invs);
        setInvoices(invs);
      } catch (err: any) {
        console.error('Error fetching billing data:', err);
        setError(err.message || 'Failed to load billing information');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // const handleManageSubscription = async () => {
  //   if (!user?.id || !subscription) return;

  //   try {
  //     setRedirecting(true);

  //     // Get customer ID
  //     const { data: customer, error: customerError } = await supabase
  //       .from('stripe_customers')
  //       .select('customer_id')
  //       .eq('user_id', user.id)
  //       .limit(1)
  //       .single();

  //     if (customerError) throw customerError;
  //     if (!customer) throw new Error('No customer record found');

  //     // Create portal session
  //     const { url } = await stripeService.createCustomerPortalSession(
  //       customer.customer_id,
  //       window.location.href // Return to current page after portal session
  //     );

  //     // Redirect to Stripe Customer Portal
  //     window.open(url, '_blank', 'noopener,noreferrer');
  //     setRedirecting(false);
  //   } catch (err: any) {
  //     console.error('Error redirecting to customer portal:', err);
  //     setError(err.message || 'Failed to redirect to billing portal');
  //     setRedirecting(false);

  //     // Show error to user
  //     toast.error(err.message || 'Failed to access billing portal');
  //   }
  // };

  const handleManageSubscription = async () => {
    if (!user?.id) {
      toast.error('User not found');
      return;
    }

    try {
      setRedirecting(true);

      // Get customer ID
      const { data: customer, error: customerError } = await supabase
        .from('stripe_customers')
        .select('customer_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();

      if (customerError) throw customerError;
      if (!customer?.customer_id) throw new Error('No customer record found');

      // Create portal session with explicit return URL
      const returnUrl = `${window.location.origin}/billing-dashboard`;
      const { url, error: portalError } = await stripeService.createCustomerPortalSession(
        customer.customer_id,
        returnUrl // Explicit return URL instead of window.location.href
      );

      if (portalError) throw portalError;
      if (!url) throw new Error('Failed to create portal session');

      // Open portal in same tab (better UX for mobile)
      window.location.href = url;

      // No need to setRedirecting(false) here as we're navigating away
    } catch (err: any) {
      console.error('Error redirecting to customer portal:', err);
      setRedirecting(false);
      toast.error(err.message || 'Failed to access billing portal');
    }
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp < 100000) {
      return 'a future date';
    }

    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-lime-green" />;
      case 'past_due':
      case 'unpaid':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'trialing':
      case 'incomplete':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <CheckCircle className="w-4 h-4 text-slate-gray" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-lime-green bg-lime-green/10 border-lime-green/30';
      case 'active_until_cancelation':
        console.log("active_until_cancelation in getStatusColor");
        return 'text-lime-red bg-red/10 border-lime-red/30';
      case 'trialing':
        return 'text-yellow-500 bg-yellow-50 border-yellow-200';
      case 'incomplete':
        return 'text-yellow-500 bg-yellow-50 border-yellow-200';
      case 'incomplete_expired':
        return 'text-red-500 bg-red-50 border-red-200';
      case 'past_due':
        return 'text-red-500 bg-red-50 border-red-200';
      case 'unpaid':
        return 'text-red-500 bg-red-50 border-red-200';
      case 'canceled':
        return 'text-gray-500 bg-gray-50 border-gray-200';
      case 'cancelled':
        return 'text-gray-500 bg-gray-50 border-gray-200';  
      case 'canceling':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-slate-gray bg-slate-gray/10 border-slate-gray/30';
    }
  };

  const getPlanName = (priceId: string) => {
    // Use the actual price ID from stripe-config.ts
    if (priceId === 'price_1RkAdnGgeEGrzrONsu86JLRr') {
      return 'CDMO Premium Access';
    }
    return 'Custom Plan';
  };

  const getBillingCycle = (priceId: string) => {
    // For now, all plans are monthly based on the current configuration
    return 'Monthly';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-charcoal mb-2">Loading Billing Information</h2>
          <p className="text-slate-gray">Fetching your subscription details...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50 max-w-md"
        >
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-charcoal mb-2">Error Loading Billing</h2>
          <p className="text-slate-gray mb-4">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }


  const formatBillingDate = (subscription: any) => {
    if (!subscription) return 'No active subscription';
    console.log(subscription, "subscription in formatBillingDate");

    // Handle cases where subscription is active but date is missing
    if ((subscription.status === 'active' || subscription.status === 'trialing') &&
      !subscription.current_period_end) {
      return 'Billing cycle updating...';
    }

    if (subscription.status === 'inactive' || subscription.status === 'canceled') {
      return 'No upcoming billing';
    }

    if (subscription.cancel_at_period_end) {
      return `Access until ${formatDate(subscription.current_period_end)}`;
    }

    return subscription.current_period_end
      ? `Next billing on ${formatDate(subscription.current_period_end)}`
      : 'Billing cycle updating...'; // More user-friendly message
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-charcoal">Billing & Subscription</h1>
                <p className="text-slate-gray">
                  Manage your subscription, payment methods, and view invoices
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/cdmo-dashboard')}
                aria-label='Back to Dashboard'
                className="px-4 py-2 border border-slate-gray/30 text-slate-gray hover:bg-light-gray rounded-xl transition-all duration-300 font-medium"
              >
                Back to Dashboard
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Subscription Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden mb-8"
        >
          <div className="px-6 py-5 border-b border-slate-gray/20">
            <h2 className="text-xl font-bold text-charcoal">Subscription Details</h2>
          </div>
          <div className="px-6 py-5">
            {subscription ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-electric-blue/5 to-deep-indigo/5 rounded-xl p-4 border border-electric-blue/20">
                  <div className="flex items-center mb-2">
                    <Building2 className="w-5 h-5 text-electric-blue mr-2" />
                    <h3 className="text-sm font-medium text-slate-gray">Current Plan</h3>
                  </div>
                  <p className="text-lg font-bold text-charcoal">
                    {getPlanName(subscription.price_id)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-lime-green/5 to-soft-teal/5 rounded-xl p-4 border border-lime-green/20">
                  <div className="flex items-center mb-2">
                    {getStatusIcon(subscription.status)}
                    <h3 className="text-sm font-medium text-slate-gray ml-2">Status</h3>
                  </div>
                  <span className={`px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(subscription.status)}`}>
                   {subscription.status == "active"? "Active" : subscription.status =="active_until_cancelation" ? "Active until cancelation" :"Inactive"}
                  </span>
                </div>

                <div className="bg-gradient-to-br from-soft-teal/5 to-lime-green/5 rounded-xl p-4 border border-soft-teal/20">
                  <div className="flex items-center mb-2">
                    <Calendar className="w-5 h-5 text-soft-teal mr-2" />
                    <h3 className="text-sm font-medium text-slate-gray">Billing Cycle</h3>
                  </div>
                  <p className="text-lg font-bold text-charcoal">
                    {getBillingCycle(subscription.price_id)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-sky-blue/5 to-electric-blue/5 rounded-xl p-4 border border-sky-blue/20">
                  <div className="flex items-center mb-2">
                    <Calendar className="w-5 h-5 text-sky-blue mr-2" />
                    <h3 className="text-sm font-medium text-slate-gray"> { subscription.status === "active" ? "Next Billing" : "Billing End" }</h3>
                  </div>
                  <p className="text-lg font-bold text-charcoal">
                    {(subscription.current_period_end && subscription.status === "active") && formatDate(subscription.current_period_end)
                    }
                    {subscription.current_period_end && subscription.status == "active_until_cancelation"
                      && formatDate(subscription.current_period_end)
                    }
                  </p>
                </div>             
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="w-16 h-16 text-slate-gray/50 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-charcoal mb-2">No Active Subscription</h3>
                <p className="text-slate-gray mb-6">Get started with a CDMO plan to access all features</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  View Plans
                </motion.button>
              </div>
            )}
          </div>

          {subscription && (
            <div className="px-6 py-4 bg-gradient-to-r from-electric-blue/5 to-deep-indigo/5 border-t border-slate-gray/20 flex justify-end">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleManageSubscription}
                disabled={redirecting || !subscription}
                className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Manage subscription in Stripe Customer Portal"
                
              >
                {redirecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Redirecting...
                  </>
                ) : (
                  <>
                    Manage Subscription
                    <ArrowRight className="w-4 h-4 ml-2" target='_blank' />
                  </>
                )}
              </motion.button>
            </div>
          )}
        </motion.div>

        {/* Invoices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden"
        >
          <div className="px-6 py-5 border-b border-slate-gray/20">
            <h2 className="text-xl font-bold text-charcoal">Invoices</h2>
            <p className="text-sm text-slate-gray mt-1">View and download your billing history</p>
          </div>
          <div className="divide-y divide-slate-gray/20">
            {invoices.length > 0 ? (
              invoices.map((invoice, index) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="px-6 py-4 flex justify-between items-center hover:bg-slate-gray/5 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-electric-blue/10 to-deep-indigo/10 rounded-lg flex items-center justify-center mr-4">
                      <FileText className="w-5 h-5 text-electric-blue" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">
                        Invoice #{invoice.number || invoice.id.slice(-8)}
                      </p>
                      <p className="text-sm text-slate-gray">
                        {new Date(invoice.created * 1000).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                      <p className="text-sm text-slate-gray">
                        ${(invoice.amount_paid / 100).toFixed(2)} • {invoice.status.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gradient-to-r from-electric-blue/10 to-deep-indigo/10 text-electric-blue hover:from-electric-blue/20 hover:to-deep-indigo/20 rounded-lg transition-all duration-300 flex items-center text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View
                  </motion.a>
                </motion.div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <FileText className="w-16 h-16 text-slate-gray/50 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-charcoal mb-2">No Invoices Found</h3>
                <p className="text-slate-gray">Your billing history will appear here once you have an active subscription</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Payment Method Info */}
        {subscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden mt-8"
          >
            <div className="px-6 py-5 border-b border-slate-gray/20">
              <h2 className="text-xl font-bold text-charcoal">Payment Method</h2>
              <p className="text-sm text-slate-gray mt-1">Your current payment method details</p>
            </div>
            <div className="px-6 py-5">
              {subscription.payment_method_last4 ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-lime-green/10 to-soft-teal/10 rounded-lg flex items-center justify-center mr-4">
                      <CreditCard className="w-5 h-5 text-lime-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">
                        {subscription.payment_method_brand} •••• {subscription.payment_method_last4}
                      </p>
                      <p className="text-sm text-slate-gray">Primary payment method</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-lime-green/10 text-lime-green rounded-lg text-sm font-medium">
                    Active
                  </span>
                </div>
              ) : 
              subscription && subscription?.status === 'active' || subscription.status === "active_until_cancelation" ? (<div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-lime-green/10 to-soft-teal/10 rounded-lg flex items-center justify-center mr-4">
                      <CreditCard className="w-5 h-5 text-lime-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-charcoal">
                        {subscription.payment_method_brand} ••••  •••• •••• {4242}
                      </p>
                      <p className="text-sm text-slate-gray">Primary payment method</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-lime-green/10 text-lime-green rounded-lg text-sm font-medium">
                    Active
                  </span>
                </div>
                ) : (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-slate-gray/50 mx-auto mb-4" />
                  <p className="text-slate-gray">Payment method information not available</p>
                  <p className="text-sm text-slate-gray mt-1">Update your payment method in the billing portal</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;